import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import {
  checkLoadout,
  emptyKit,
  fetchLoadout,
  kitOf,
  problemsOf,
  ROSTER_ROLES,
  saveLoadoutKit,
  searchGear,
  type GearHit,
  type GearPage,
  type KitContainer,
  type KitWeapon,
  type LoadoutKit,
  type LoadoutProblem,
} from '../api/catalog';
import { ApiError } from '../api/http';
import { ca } from '../i18n/ca';
import { ModOriginChip } from './ModOrigin';
import {
  applyPick,
  classesOf,
  kindsFor,
  problemText,
  targetKey,
  targetLabel,
  type ContainerSlot,
  type OneSlot,
  type Part,
  type Target,
  type WeaponSlot,
} from './loadoutKit';
import { Shell } from './Shell';

const e = ca.loadoutEditor;

const sideKeys = ['west', 'east', 'resistance', 'civilian'];

/**
 * The loadout editor (roadmap item faccions-i-equipaments). Opens one saved
 * loadout, or a new one, and lets an editor choose its uniform, vest, headgear,
 * backpack and weapons with their magazines, each from the classes of the mod
 * set and nothing else. The picker reads one page at a time from the gear
 * search (`/api/catalog/{hash}/gear`); the kit is saved through the
 * platform's own check, which says which piece fails and why.
 *
 * Routes (kept stable, other pages link here): `/catalog/loadouts/:id` for a
 * saved loadout and `/catalog/loadouts/new` for a new one, both with
 * `?hash=<mod set>` (a saved loadout falls back to the mod set it was saved
 * under) and, for a new one, `&role=` and `&side=`.
 */
export function LoadoutEditor() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const isNew = id === undefined || id === 'new';
  const loadoutId = isNew ? null : Number(id);

  const [loaded, setLoaded] = useState(isNew);
  const [missing, setMissing] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState(params.get('role') ?? '');
  const [side, setSide] = useState(params.get('side') ?? '');
  const [modSet, setModSet] = useState<string | null>(params.get('hash'));
  const [kit, setKit] = useState<LoadoutKit>(emptyKit);
  const [names, setNames] = useState<Record<string, GearHit>>({});
  const [target, setTarget] = useState<Target | null>(null);
  const pickerRef = useRef<HTMLElement | null>(null);
  const [kindFilter, setKindFilter] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [limit, setLimit] = useState(50);
  const [page, setPage] = useState<GearPage | null>(null);
  const [pickError, setPickError] = useState(false);
  const [problems, setProblems] = useState<LoadoutProblem[]>([]);
  const [checking, setChecking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const remember = useCallback((hits: GearHit[]) => {
    setNames((old) => {
      const next = { ...old };
      hits.forEach((h) => (next[h.className.toLowerCase()] = h));
      return next;
    });
  }, []);

  // A saved loadout: its name, role, side and kit.
  useEffect(() => {
    if (loadoutId === null) return undefined;
    let live = true;
    setLoaded(false);
    void fetchLoadout(loadoutId)
      .then((l) => {
        if (!live) return;
        setName(l.name);
        setRole(l.role ?? '');
        setSide(l.side ?? '');
        setKit(kitOf(l.loadoutJson));
        setModSet((h) => h ?? l.modSetHash);
        setLoaded(true);
      })
      .catch(() => live && setMissing(true));
    return () => {
      live = false;
    };
  }, [loadoutId]);

  // What each class the kit holds is called: one call for the whole kit when it is opened.
  useEffect(() => {
    if (!loaded || !modSet) return undefined;
    const wanted = classesOf(kit).filter((c) => !names[c.toLowerCase()]);
    if (wanted.length === 0) return undefined;
    let live = true;
    void searchGear(modSet, { classes: wanted, limit: 500 })
      .then((p) => live && remember(p.items))
      .catch(() => undefined);
    return () => {
      live = false;
    };
    // The names are looked up when the kit is opened or a piece is added from outside the picker only.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loaded, modSet]);

  // The picker: one page of what the mod set has, for the target's kind and the word typed.
  const kinds = useMemo(() => (target ? kindsFor(target) : []), [target]);
  const activeKind = kindFilter && kinds.includes(kindFilter) ? kindFilter : (kinds[0] ?? null);
  const tKey = target ? targetKey(target) : '';
  useEffect(() => {
    setKindFilter(null);
    setLimit(50);
    // On a phone the picker sits under the pieces: bring it into view when a piece asks for it.
    if (tKey && window.matchMedia?.('(max-width: 900px)').matches) {
      pickerRef.current?.scrollIntoView?.({ block: 'start' });
    }
  }, [tKey]);
  useEffect(() => {
    setLimit(50);
  }, [query, activeKind]);
  useEffect(() => {
    if (!target || !modSet || !activeKind) {
      setPage(null);
      return undefined;
    }
    let live = true;
    const t = setTimeout(() => {
      void searchGear(modSet, { kind: activeKind, q: query.trim() || null, limit })
        .then((p) => {
          if (!live) return;
          setPickError(false);
          setPage(p);
          remember(p.items);
        })
        .catch(() => live && setPickError(true));
    }, 250);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [tKey, modSet, activeKind, query, limit, target, remember]);

  // The kit read against the mod set while it is built, so a fault shows before the save.
  useEffect(() => {
    if (!loaded || !modSet || classesOf(kit).length === 0) {
      setProblems([]);
      return undefined;
    }
    let live = true;
    setChecking(true);
    const t = setTimeout(() => {
      void checkLoadout(kit, modSet)
        .then((c) => live && setProblems(c.problems))
        .catch(() => live && setProblems([]))
        .finally(() => live && setChecking(false));
    }, 500);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [kit, modSet, loaded]);

  const change = (next: LoadoutKit) => {
    setKit(next);
    setMessage(null);
    setError(null);
  };
  const pick = (hit: GearHit) => {
    if (!target) return;
    remember([hit]);
    change(applyPick(kit, target, hit.className));
  };
  const setWeaponField = (slot: WeaponSlot, patch: Partial<KitWeapon>) => {
    const w = kit[slot];
    if (w) change({ ...kit, [slot]: { ...w, ...patch } });
  };
  const setContainerContents = (slot: ContainerSlot, contents: KitContainer['contents']) => {
    const c = kit[slot];
    if (c) change({ ...kit, [slot]: { ...c, contents } });
  };

  const nameOf = (cls: string | null | undefined): string => {
    if (!cls) return '';
    const hit = names[cls.toLowerCase()];
    return hit && hit.displayName && hit.displayName !== cls ? hit.displayName : cls;
  };

  const empty = classesOf(kit).length === 0;
  const blocking = problems.filter((p) => p.reason !== 'empty');
  const canSave = !busy && !!name.trim() && !!modSet && !empty;

  const save = async () => {
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      const saved = await saveLoadoutKit({
        id: loadoutId ?? undefined,
        name: name.trim(),
        role: role || null,
        side: side || null,
        kit,
        modSetHash: modSet,
      });
      setProblems([]);
      setMessage(e.saved);
      if (loadoutId === null) {
        navigate(`/catalog/loadouts/${saved.id}${modSet ? `?hash=${modSet}` : ''}`, {
          replace: true,
        });
      }
    } catch (err) {
      const refused = problemsOf(err);
      if (refused.length > 0) setProblems(refused);
      setError(
        refused.length > 0
          ? e.saveFailed
          : err instanceof ApiError
            ? (err.detail ?? err.code)
            : e.saveFailed,
      );
    } finally {
      setBusy(false);
    }
  };

  const title = isNew ? e.titleNew : e.title(name);
  const back = (
    <p className="muted small">
      <Link to="/catalog?tab=loadouts">{e.back}</Link>
    </p>
  );
  if (missing) {
    return (
      <Shell title={e.titleNew} width="wide">
        {back}
        <p className="notice warn">{e.notFound}</p>
      </Shell>
    );
  }
  if (!loaded) {
    return (
      <Shell title={title} width="wide">
        {back}
        <p className="muted small">{e.loading}</p>
      </Shell>
    );
  }

  /** The row of a chosen class: its name, the class under it when they differ, and the buttons. */
  const chosen = (cls: string | null | undefined, t: Target, extra?: ReactNode) => (
    <div className="gear-row">
      <div className="grow">
        {cls ? (
          <>
            <strong>{nameOf(cls)}</strong>
            {nameOf(cls) !== cls && <code className="small muted"> {cls}</code>}
            <ModOriginChip origin={names[cls.toLowerCase()]?.origin} />
          </>
        ) : (
          <span className="muted">{e.none}</span>
        )}
      </div>
      {extra}
      <button
        type="button"
        className={`small ${target && targetKey(target) === targetKey(t) ? 'primary' : ''}`}
        aria-label={`${cls ? e.change : e.choose}: ${targetLabel(t)}`}
        onClick={() => setTarget(t)}
      >
        <i className="bi bi-search" aria-hidden="true" /> {cls ? e.change : e.choose}
      </button>
    </div>
  );

  const removeButton = (what: string, onClick: () => void) => (
    <button type="button" className="small" aria-label={`${e.remove}: ${what}`} onClick={onClick}>
      <i className="bi bi-x-lg" aria-hidden="true" />
    </button>
  );

  const countInput = (what: string, value: number, onChange: (n: number) => void) => (
    <input
      type="number"
      className="gear-count"
      aria-label={`${e.quantity}: ${what}`}
      min={1}
      max={99}
      value={value}
      onChange={(ev) => onChange(Math.max(1, Math.min(99, Number(ev.target.value) || 1)))}
    />
  );

  const simple = (slot: OneSlot) => (
    <li key={slot}>
      <h3>{e.slots[slot]}</h3>
      {chosen(
        kit[slot],
        { type: 'one', slot },
        kit[slot] && removeButton(e.slots[slot], () => change({ ...kit, [slot]: null })),
      )}
    </li>
  );

  const container = (slot: ContainerSlot) => {
    const c = kit[slot];
    return (
      <li key={slot}>
        <h3>{e.slots[slot]}</h3>
        {chosen(
          c?.className,
          { type: 'container', slot },
          c && removeButton(e.slots[slot], () => change({ ...kit, [slot]: null })),
        )}
        {c && !c.className && <p className="notice warn small">{e.unnamedHint}</p>}
        {c && (
          <div className="gear-contents">
            <p className="muted small">
              {e.contents}
              {c.contents.length === 0 && ` · ${e.contentsEmpty}`}
            </p>
            {c.contents.map((i, k) => (
              <div className="gear-row" key={`${i.className}-${k}`}>
                <div className="grow">
                  <strong>{nameOf(i.className)}</strong>
                  {nameOf(i.className) !== i.className && (
                    <code className="small muted"> {i.className}</code>
                  )}
                </div>
                {countInput(nameOf(i.className), i.count, (n) =>
                  setContainerContents(
                    slot,
                    c.contents.map((x, j) => (j === k ? { ...x, count: n } : x)),
                  ),
                )}
                {removeButton(nameOf(i.className), () =>
                  setContainerContents(
                    slot,
                    c.contents.filter((_, j) => j !== k),
                  ),
                )}
              </div>
            ))}
            <button
              type="button"
              className="small"
              aria-label={`${e.addContent}: ${e.slots[slot]}`}
              onClick={() => setTarget({ type: 'contents', slot })}
            >
              <i className="bi bi-plus-lg" aria-hidden="true" /> {e.addContent}
            </button>
          </div>
        )}
      </li>
    );
  };

  const weapon = (slot: WeaponSlot) => {
    const w = kit[slot];
    return (
      <li key={slot}>
        <h3>{e.slots[slot]}</h3>
        {chosen(
          w?.className,
          { type: 'weapon', slot },
          w && removeButton(e.slots[slot], () => change({ ...kit, [slot]: null })),
        )}
        {w && (
          <div className="gear-contents">
            {(['optic', 'muzzle', 'laser', 'bipod'] as Part[]).map((part) => (
              <div className="gear-part-row" key={part}>
                <span className="gear-part muted small">{e.parts[part]}</span>
                {chosen(
                  w[part],
                  { type: 'part', slot, part },
                  w[part] &&
                    removeButton(e.parts[part], () => setWeaponField(slot, { [part]: null })),
                )}
              </div>
            ))}
            <p className="muted small">{e.magazines}</p>
            {w.magazines.map((m, k) => (
              <div className="gear-row" key={`${m.className}-${k}`}>
                <div className="grow">
                  <strong>{nameOf(m.className)}</strong>
                  {nameOf(m.className) !== m.className && (
                    <code className="small muted"> {m.className}</code>
                  )}
                </div>
                {countInput(nameOf(m.className), m.count, (n) =>
                  setWeaponField(slot, {
                    magazines: w.magazines.map((x, j) => (j === k ? { ...x, count: n } : x)),
                  }),
                )}
                {removeButton(nameOf(m.className), () =>
                  setWeaponField(slot, { magazines: w.magazines.filter((_, j) => j !== k) }),
                )}
              </div>
            ))}
            {w.magazines.length < 2 ? (
              <button
                type="button"
                className="small"
                aria-label={`${e.addMagazine}: ${e.slots[slot]}`}
                onClick={() => setTarget({ type: 'magazine', slot })}
              >
                <i className="bi bi-plus-lg" aria-hidden="true" /> {e.addMagazine}
              </button>
            ) : (
              <p className="muted small">{e.twoMagazines}</p>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <Shell title={title} width="wide">
      {back}
      <p className="muted small">{e.intro}</p>
      {!modSet && <p className="notice warn">{e.noModSet}</p>}

      <div className="loadout-editor">
        <div className="stack">
          <div className="card form">
            <div className="gear-meta">
              <label>
                {e.nameLabel}
                <input
                  name="loadoutName"
                  maxLength={128}
                  value={name}
                  onChange={(ev) => {
                    setName(ev.target.value);
                    setMessage(null);
                  }}
                />
              </label>
              <label>
                {e.roleLabel}
                <select name="loadoutRole" value={role} onChange={(ev) => setRole(ev.target.value)}>
                  <option value="">{e.roleNone}</option>
                  {role && !(ROSTER_ROLES as readonly string[]).includes(role) && (
                    <option value={role}>{role}</option>
                  )}
                  {ROSTER_ROLES.map((r) => (
                    <option key={r} value={r}>
                      {ca.factionRoster.roles[r] ?? r}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {e.sideLabel}
                <select name="loadoutSide" value={side} onChange={(ev) => setSide(ev.target.value)}>
                  <option value="">{e.sideNone}</option>
                  {sideKeys.map((s) => (
                    <option key={s} value={s}>
                      {ca.sideNames[s] ?? s}
                    </option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          <div className="card">
            <h2>{e.slotsTitle}</h2>
            <ul className="gear-slots">
              {simple('headgear')}
              {simple('goggles')}
              {container('uniform')}
              {container('vest')}
              {container('backpack')}
              {simple('binocular')}
            </ul>
          </div>

          <div className="card">
            <h2>{e.weaponsTitle}</h2>
            <ul className="gear-slots">
              {weapon('primary')}
              {weapon('launcher')}
              {weapon('handgun')}
            </ul>
          </div>

          <div className="card">
            <h2>{e.linkedTitle}</h2>
            <p className="muted small">{e.linkedHint}</p>
            {kit.linkedItems.map((cls) => (
              <div className="gear-row" key={cls}>
                <div className="grow">
                  <strong>{nameOf(cls)}</strong>
                  {nameOf(cls) !== cls && <code className="small muted"> {cls}</code>}
                </div>
                {removeButton(nameOf(cls), () =>
                  change({ ...kit, linkedItems: kit.linkedItems.filter((c) => c !== cls) }),
                )}
              </div>
            ))}
            <button type="button" className="small" onClick={() => setTarget({ type: 'linked' })}>
              <i className="bi bi-plus-lg" aria-hidden="true" /> {e.addLinked}
            </button>
          </div>

          <div className="card form">
            {blocking.length > 0 && (
              <div className="notice error" role="alert">
                <strong>{e.problemsTitle(blocking.length)}</strong>
                <ul className="gear-problems">
                  {blocking.map((p, i) => (
                    <li key={`${p.slot}-${p.className}-${i}`}>{problemText(p)}</li>
                  ))}
                </ul>
              </div>
            )}
            {blocking.length === 0 && !empty && modSet && (
              <p className="muted small" role="status">
                {checking ? e.checking : e.allGood}
              </p>
            )}
            {error && blocking.length === 0 && <p className="error small">{error}</p>}
            {message && (
              <p className="notice ok" role="status">
                {message}
              </p>
            )}
            <div className="row actions">
              <span className="grow" />
              <button
                type="button"
                className="primary"
                disabled={!canSave}
                onClick={() => void save()}
              >
                <i className="bi bi-check-lg" aria-hidden="true" /> {busy ? e.saving : e.save}
              </button>
            </div>
          </div>
        </div>

        <aside ref={pickerRef} className="card form gear-picker" aria-label={e.pickerTitle}>
          <h2>{e.pickerTitle}</h2>
          {!target ? (
            <p className="muted small">{e.pickerIdle}</p>
          ) : (
            <>
              <p className="small">
                <strong>{e.pickerFor(targetLabel(target))}</strong>
              </p>
              {kinds.length > 1 && (
                <div className="chips" role="group" aria-label={e.kindAll}>
                  {kinds.map((k) => (
                    <button
                      type="button"
                      key={k}
                      className={`chip ${activeKind === k ? 'ok' : ''}`}
                      aria-pressed={activeKind === k}
                      onClick={() => setKindFilter(k)}
                    >
                      {e.kinds[k] ?? k}
                      {page?.kinds[k] !== undefined && ` · ${page.kinds[k]}`}
                    </button>
                  ))}
                </div>
              )}
              <label>
                {e.searchLabel}
                <input
                  name="gearSearch"
                  type="search"
                  value={query}
                  onChange={(ev) => setQuery(ev.target.value)}
                />
              </label>
              {pickError && <p className="error small">{e.noCatalogue}</p>}
              {page && page.items.length === 0 && !pickError && (
                <p className="muted small">{e.noResults}</p>
              )}
              {page && page.items.length > 0 && (
                <>
                  <p className="muted small">{e.results(page.items.length, page.total)}</p>
                  <ul className="list gear-results" aria-label={e.pickerTitle}>
                    {page.items.map((h) => (
                      <li key={h.className}>
                        <div className="grow">
                          <strong>{h.displayName}</strong>
                          {h.displayName !== h.className && (
                            <code className="small muted"> {h.className}</code>
                          )}
                          <ModOriginChip origin={h.origin} />
                        </div>
                        <button
                          type="button"
                          className="small primary"
                          aria-label={e.pickIt(h.displayName)}
                          onClick={() => pick(h)}
                        >
                          {e.choose}
                        </button>
                      </li>
                    ))}
                  </ul>
                  {page.items.length < page.total && (
                    <button type="button" onClick={() => setLimit((n) => n + 50)}>
                      {e.more}
                    </button>
                  )}
                </>
              )}
            </>
          )}
        </aside>
      </div>
    </Shell>
  );
}
